import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';

const Plasma = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet 
        title="MCT Plasma - Enhanced PRP Therapy"
        description="Stimulate platelets and boost growth factor release with MCT's photothermal biomodulation technology, resulting in faster and better regenerative outcomes."
        keywords="MCT plasma, PRP therapy, platelet-rich plasma, growth factors, photothermal biomodulation, platelet performance, regenerative medicine"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-white via-teal-50/20 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light mb-8 text-gray-900 animate-fade-in" style={{ fontFamily: 'Raleway, sans-serif' }}>
            MCT <span className="text-teal-500 font-normal">Plasma</span>
          </h1>
          <p className="text-2xl md:text-3xl text-gray-700 font-light max-w-4xl mx-auto leading-relaxed animate-fade-in">
            Stimulates platelets and boosts growth factor release, resulting in faster and better outcomes
          </p>
        </div>
      </section>

      {/* Process Flow Diagram */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 shadow-xl">
            <img
              src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/wmtz3fg6_image.png"
              alt="Autologous Biological Products Process Flow"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Three Benefits Grid */}
      <section className="py-32 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Growth Factors Release */}
            <div className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="flex justify-center mb-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/2dlttd35_growth%20factors.png"
                    alt="Growth Factors"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4 text-center">
                Growth Factors Release
              </h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Photo-Thermal boosted platelets release a higher amount of growth factors in a controlled and sustained way for a more physiological effect
              </p>
            </div>

            {/* Platelet Performance */}
            <div className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="flex justify-center mb-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/ari74uq3_platelet%20performance.png"
                    alt="Platelet Performance"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4 text-center">
                Platelet Performance
              </h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Photothermal stimulated plasma contains less proinflammatory cytokines and has a more physiological profile. It improves platelet activation, reducing VASP and increasing p-38 phosphorylation. Cold primes platelets and facilitates reactivity once injected
              </p>
            </div>

            {/* Procedure Comfort */}
            <div className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="flex justify-center mb-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/6ye1m6h4_image.png"
                    alt="Procedure Comfort"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4 text-center">
                Procedure Comfort
              </h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Cold helps platelet kinetics after activation, not before, and induces a delay in clot formation. A less dense plasma and the sensation of cold liquid results in a much more comfortable procedure
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Photobiomodulation & Thermal Sections */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Photobiomodulation */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h2 className="text-4xl font-light text-gray-900" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Photobiomodulation
              </h2>
              <div className="space-y-4 text-lg text-gray-600 font-light leading-relaxed">
                <p>
                  Light provides a non-invasive, non-toxic way of stimulating cellular metabolism. Ultimately, the energy absorbed at the mitochondria enhances cellular ATP production.
                </p>
                <p>
                  Photothermal-stimulation of PRP generates a state-of-the-art regenerative preparate. It achieves improved clinical results due to a more prolonged and sustained growth factors release and a reduced pro-inflammatory cytokine content.
                </p>
              </div>
            </div>

            {/* Thermal Biomodulation */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h2 className="text-4xl font-light text-gray-900" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Thermal Biomodulation
              </h2>
              <div className="space-y-4 text-lg text-gray-600 font-light leading-relaxed">
                <p>
                  As with light, temperature has a huge body of evidence that backs up its effectiveness and safe utilization. Thermal stimulation prepares platelets to work more and better, increases their reactiveness and shows a myriad of activation markers.
                </p>
                <p>
                  Platelet primed functionality also determines an increment in growth factors. Since thermal boosting delays the clot onset and primes platelet aggregation only after activation, this results in a less dense regenerative product, improving clinical results with a more convenient injection experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Experience Enhanced <span className="text-teal-500 font-normal">PRP Therapy</span>
          </h2>
          <p className="text-xl text-gray-600 font-light mb-10 leading-relaxed">
            Discover how MCT's photothermal biomodulation can transform your regenerative medicine practice
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white px-12 py-6 rounded-2xl font-medium transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] text-lg"
          >
            Contact Us
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Plasma;
